import type { GeneratedCertificate } from '../types/crm';
import { findIssuedCertificateNumber } from '../services/issuedCertificatesService';

export type CertificateNumberIdentity = {
  currentId?: string;
  sessionId?: string;
  attendeeId?: string;
  attendeeName?: string;
  courseName?: string;
  courseDate?: string;
  customerId?: string;
};

export function isAllocatedCertificateNumber(id: string | undefined | null): boolean {
  return !!id && /^TT-\d+$/i.test(String(id).trim());
}

export function parseSessionIdFromCertId(certId: string | undefined | null): string | undefined {
  const match = String(certId || '').match(/^session-([0-9a-f-]{36})-/i);
  return match?.[1];
}

export function parseAttendeeIdFromCertificateId(certId: string | undefined | null): string | undefined {
  const match = String(certId || '').match(/^session-[0-9a-f-]{36}-([0-9a-f-]{36})$/i);
  return match?.[1];
}

export function provisionalSessionCertificateId(sessionId: string, attendeeId: string): string {
  return `session-${sessionId}-${attendeeId}`;
}

function normalizePersonName(value: string | undefined | null): string {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
}

function toIsoDatePrefix(value: string | undefined | null): string {
  const raw = String(value || '').trim();
  const ymd = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return ymd ? `${ymd[1]}-${ymd[2]}-${ymd[3]}` : raw.slice(0, 10);
}

function pickLowestAllocatedNumber(ids: string[]): string | undefined {
  const vins = ids.map((id) => String(id || '').trim()).filter(isAllocatedCertificateNumber);
  if (!vins.length) return undefined;
  vins.sort((a, b) => {
    const na = Number.parseInt(a.replace(/^TT-/i, ''), 10);
    const nb = Number.parseInt(b.replace(/^TT-/i, ''), 10);
    if (Number.isFinite(na) && Number.isFinite(nb) && na !== nb) return na - nb;
    return a.localeCompare(b);
  });
  return vins[0];
}

export function generatedCertificateMatchesIdentity(
  cert: GeneratedCertificate,
  identity: CertificateNumberIdentity
): boolean {
  const currentId = String(identity.currentId || '').trim();
  if (currentId && cert.id === currentId) return true;

  const provisionalId =
    identity.sessionId && identity.attendeeId
      ? provisionalSessionCertificateId(identity.sessionId, identity.attendeeId)
      : undefined;
  if (provisionalId && cert.id === provisionalId) return true;

  const certSessionId = cert.sessionId || parseSessionIdFromCertId(cert.id);
  const certAttendeeId = cert.attendeeId || parseAttendeeIdFromCertificateId(cert.id);

  if (identity.sessionId && certSessionId === identity.sessionId) {
    if (identity.attendeeId && certAttendeeId) return identity.attendeeId === certAttendeeId;
    if (identity.attendeeId && cert.attendeeId) return identity.attendeeId === cert.attendeeId;
    if (identity.attendeeName && normalizePersonName(cert.attendeeName) === normalizePersonName(identity.attendeeName)) {
      return true;
    }
    // Individual customer records hold at most one cert per session.
    if (!identity.attendeeId && !certAttendeeId) return true;
    return false;
  }

  if (
    identity.attendeeName &&
    identity.courseName &&
    identity.courseDate &&
    normalizePersonName(cert.attendeeName) === normalizePersonName(identity.attendeeName) &&
    normalizePersonName(cert.courseName) === normalizePersonName(identity.courseName) &&
    toIsoDatePrefix(cert.courseDate) === toIsoDatePrefix(identity.courseDate)
  ) {
    return true;
  }

  return false;
}

export function findMatchingGeneratedCertificateIndex(
  certs: GeneratedCertificate[],
  identity: CertificateNumberIdentity
): number {
  const currentId = String(identity.currentId || '').trim();
  if (currentId) {
    const exact = certs.findIndex((cert) => cert.id === currentId);
    if (exact >= 0) return exact;
  }
  const provisionalId =
    identity.sessionId && identity.attendeeId
      ? provisionalSessionCertificateId(identity.sessionId, identity.attendeeId)
      : undefined;
  if (provisionalId) {
    const byProvisional = certs.findIndex((cert) => cert.id === provisionalId);
    if (byProvisional >= 0) return byProvisional;
  }
  return certs.findIndex((cert) => generatedCertificateMatchesIdentity(cert, identity));
}

export function findExistingCertificateNumberFromCerts(
  certs: GeneratedCertificate[] | undefined | null,
  identity: CertificateNumberIdentity
): string | undefined {
  if (isAllocatedCertificateNumber(identity.currentId)) {
    return String(identity.currentId).trim();
  }
  const list = Array.isArray(certs) ? certs : [];
  const matched = list.filter((cert) => generatedCertificateMatchesIdentity(cert, identity));
  return pickLowestAllocatedNumber(matched.map((cert) => cert.id));
}

/** Resolve a previously assigned TT- number, or undefined if a new one must be allocated. */
export async function findExistingCertificateNumber(
  identity: CertificateNumberIdentity,
  existingCerts?: GeneratedCertificate[] | null
): Promise<string | undefined> {
  const fromCerts = findExistingCertificateNumberFromCerts(existingCerts, identity);
  if (fromCerts) return fromCerts;
  return findIssuedCertificateNumber({
    customerId: identity.customerId,
    attendeeName: identity.attendeeName,
    courseName: identity.courseName,
    courseDate: identity.courseDate,
  });
}
